import { useCallback, useEffect, useMemo, useState } from 'react'

import { useRequestUserConfigContext } from '../../../hooks/request'
import { encodeToBase64 } from '../../../utils/encoding-utils'
import { dataEventClient, searchClient } from '../../../clients'
import SearchRegionNodeTransport from '../../../transports/search-region-node-transport'
import { useAsyncCallback } from '../../../hooks/async'
import { useSessionEffect } from '../../../hooks/session'
import { mobileBreakpoint } from 'react-components'
import { isServer } from '../../../utils/platform-utils'

// ... imports
import SearchResultListingSearchTransport from '../../../transports/search-result-listing-search-transport'
import { HeadTransport } from '../../../transports'
import { useSelector } from 'react-redux'
import { getFiltersConfiguration } from '../../../redux/filters'
import { LIST_VIEW_CONFIG } from '../../../constants/search-result-listing'
import headClient from '../../../clients/head-client'

interface UseSearchRegionNodeResult {
    searchRegionNode: SearchRegionNodeTransport | null
    head: HeadTransport | null
    isSearchRegionNodeLoading: boolean
    searchRegionNodeError: Error | null
}

interface UseSearchRegionNodeResultV2 {
    searchRegionNode: SearchRegionNodeTransport | null
    isSearchRegionNodeLoading: boolean
    searchRegionNodeError: Error | null
}

interface UseSearchResultListingHeadTransport {
    head: HeadTransport | null
    isHeadResponseLoading: boolean
    headResponseError: Error | null
}

/**
 * Hook to fetch search region node (and head) independently
 * Delete it later
 */
export const useSearchRegionNode = (): UseSearchRegionNodeResult => {
    const user = useRequestUserConfigContext()

    const _getSearchRegionNode = useCallback(() => {
        const filter = encodeToBase64(window.location.pathname + window.location.search)
        return searchClient.getSearchRegionNode({ user, filter }).then(response => response.data)
    }, [user])

    const {
        data,
        isLoading: isSearchRegionNodeLoading,
        error: searchRegionNodeError,
        callback: fetchSearchRegionNode,
    } = useAsyncCallback<SearchResultListingSearchTransport>(_getSearchRegionNode)

    useSessionEffect(() => {
        fetchSearchRegionNode()
    }, [fetchSearchRegionNode])

    return {
        searchRegionNode: data?.fastlaneSearchRegionNodeResponse ?? null,
        head: data?.headResponse ?? null,
        isSearchRegionNodeLoading,
        searchRegionNodeError,
    }
}

/**
 * Hook to fetch search region node (and head) independently
 */
export const useSearchRegionNodeV2 = (): UseSearchRegionNodeResultV2 => {
    const user = useRequestUserConfigContext()

    const _getSearchRegionNode = useCallback(() => {
        const filter = encodeToBase64(window.location.pathname + window.location.search)
        return searchClient.getSearchRegionNodeV2({ user, filter }).then(response => response.data)
    }, [user])

    const {
        data,
        isLoading: isSearchRegionNodeLoading,
        error: searchRegionNodeError,
        callback: fetchSearchRegionNode,
    } = useAsyncCallback<SearchRegionNodeTransport>(_getSearchRegionNode)

    useSessionEffect(() => {
        fetchSearchRegionNode()
    }, [fetchSearchRegionNode])

    return {
        searchRegionNode: data ?? null,
        isSearchRegionNodeLoading,
        searchRegionNodeError,
    }
}

export const useSearchResultListingHead = (): UseSearchResultListingHeadTransport => {
    const user = useRequestUserConfigContext()
    const _getHeadResponse = useCallback(() => {
        const filter = encodeToBase64(window.location.pathname + window.location.search)
        return headClient.getSRLHead({ user, filter }).then(response => response.data)
    }, [user])

    const {
        data,
        isLoading: isHeadResponseLoading,
        error: headResponseError,
        callback: fetchHeadResponse,
    } = useAsyncCallback<HeadTransport>(_getHeadResponse)

    useSessionEffect(() => {
        fetchHeadResponse()
    }, [fetchHeadResponse])

    return {
        head: data ?? null,
        isHeadResponseLoading,
        headResponseError,
    }
}

/**
 * Hook that sends a search result listing data event to BFF.
 * Fire-and-forget: triggers on every page load after session is ready.
 */
export const useSendSearchResultListingDataEvent = (): void => {
    const user = useRequestUserConfigContext()

    useSessionEffect(() => {
        const filter = encodeToBase64(window.location.pathname + window.location.search)
        dataEventClient.sendSearchResultListingEvent({ user, filter }).catch(() => {
            // Fire-and-forget: silently ignore errors
        })
    }, [user])
}

export const useActiveFilters = () => {
    const filtersConfiguration = useSelector(getFiltersConfiguration)
    // const commitFiltersConfiguration = useSelector(getCommittedFiltersConfiguration)
    const activeFilterConfiguration = filtersConfiguration
    return useMemo(() => {
        if (!activeFilterConfiguration) {
            return []
        }
        return activeFilterConfiguration.filters.flatMap(
            filter =>
                filter.items
                    ?.filter(item => item.checked)
                    .map(item => ({
                        label: item.title?.trim() || filter.title,
                        resetLink: item.encodedLink,
                    })) || [],
        )
    }, [filtersConfiguration])
}

const getInitialState = () => {
    if (isServer) {
        return true
    }
    return window.matchMedia(`(max-width: ${mobileBreakpoint.max}px)`).matches
}

const useGridView = () => {
    const [showGridView, setShowGridView] = useState(false)
    const [isMobile, setIsMobile] = useState(false)

    useEffect(() => {
        const media = window.matchMedia(`(max-width: ${mobileBreakpoint.max}px)`)

        setIsMobile(media.matches)
        setShowGridView(media.matches)

        const handleChange = (e: MediaQueryListEvent) => {
            setIsMobile(e.matches)
            if (e.matches) {
                setShowGridView(true)
            }
        }

        media.addEventListener('change', handleChange)
        return () => media.removeEventListener('change', handleChange)
    }, [])

    const toggleView = () => setShowGridView(prev => !prev)

    const gridItemsPerRow = isMobile
        ? LIST_VIEW_CONFIG.MOBILE_ITEMS_PER_ROW
        : showGridView
        ? LIST_VIEW_CONFIG.GRID_ITEMS_PER_ROW
        : LIST_VIEW_CONFIG.LIST_ITEMS_PER_ROW

    return {
        isGridView: showGridView,
        gridItemsPerRow,
        toggleView,
    }
}

export default useGridView
